import React, { FC, useState, ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, FormControl, Grid, TextField } from '@mui/material';
import { PanNumber } from 'models/pan-number';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';

type AddUpdatePanNumberDialogProps = {
    panNumber?: PanNumber;
    open: boolean;
    onDialogClose: () => void;
    onPanNumberAdd: (panNumber: PanNumber) => void;
    onPanNumberUpdate: (panNumber: PanNumber) => void;
};

const AddUpdatePanNumberDialog: FC<AddUpdatePanNumberDialogProps> = ({
    panNumber,
    open,
    onDialogClose,
    onPanNumberAdd,
    onPanNumberUpdate,
}: AddUpdatePanNumberDialogProps) => {
    const { t: translate } = useTranslation(['common', 'lab-settings']);
    const [panNumberState, setPanNumberState] = useState<PanNumber>({} as PanNumber);

    useEffect(() => {
        if (panNumber) {
            setPanNumberState(panNumber);
        }
    }, [panNumber]);

    const handleValueChange = (event: ChangeEvent<HTMLInputElement>) => {
        setPanNumberState({ ...panNumberState, [event.target.name]: event.target.value });
    };

    const handleDialogClose = () => {
        setPanNumberState({} as PanNumber);
        onDialogClose();
    };

    const handleAdd = () => {
        onPanNumberAdd(panNumberState);
    };

    const handleUpdate = () => {
        onPanNumberUpdate(panNumberState);
    };

    const dialogActions = (
        <>
            <Button onClick={handleDialogClose}>{translate('common:CANCEL')}</Button>
            <Button
                disabled={!panNumberState.value}
                onClick={panNumberState?._id ? handleUpdate : handleAdd}
                variant="contained"
                type="submit"
            >
                {panNumberState?._id ? translate('common:UPDATE') : translate('common:ADD')}
            </Button>
        </>
    );

    return (
        <DHDialog
            maxWidth="sm"
            fullWidth
            open={open}
            onClose={handleDialogClose}
            title={
                panNumberState?._id
                    ? translate('lab-settings:EDIT_PAN_NUMBER')
                    : translate('lab-settings:ADD_PAN_NUMBER')
            }
            actions={dialogActions}
        >
            <Box mt={1}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <FormControl margin="dense" fullWidth>
                            <TextField
                                value={panNumberState?.value || ''}
                                name="value"
                                onChange={handleValueChange}
                                required
                                id="value"
                                label={translate('lab-settings:PAN_NUMBER_VALUE')}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl margin="dense" fullWidth>
                            <TextField
                                value={panNumberState?.description || ''}
                                name="description"
                                onChange={handleValueChange}
                                id="description"
                                label={translate('common:DESCRIPTION')}
                                multiline
                                rows={3}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>
        </DHDialog>
    );
};

export default AddUpdatePanNumberDialog;
